import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useHeaderExtra, useHeaderRight } from '@/contexts/header.context'
import { useAppAuth } from '@/contexts/app-auth.context'
import { DirectoryProvider, useDirectory } from '@/contexts/directory.context'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { useDocumentHead } from '@/hooks/use-document-head'
import { ApiError } from '@/lib/api/base-client'
import { actionsCatalogApiClient } from '@/lib/api/actions-catalog.client'
import type { ContactActionType } from '@/lib/api/directory.client'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import {
  mapStaffUserToItem,
  mapTaskCategoryToItem,
  mapVendorToItem,
} from '@/lib/api/integrations/buildium.utils'
import { mapAssigneeToItem } from '@/lib/api/integrations/hubspot.utils'
import { buildCatalogDeleteMessage } from '@/lib/actions/describe'
import {
  buildPushOptions,
  contactChannelOf,
  editorToWorkflowGroups,
  workflowToEditorGroups,
} from '@/lib/handoff-rule.converters'
import { parseMentions } from '@/lib/handoff-mentions'
import { pathFor, ROUTE_REGISTRY } from '@/lib/routes'
import { confirmDialog } from '@/lib/utils/window-utils'
import {
  type ActionRow,
  type ActionRowType,
  type PushFieldDropdownItem,
  type PushOption,
  type TransferSettings,
} from '@/components/actions/ActionEditor'
import { ActionCatalogForm } from '@/components/actions/ActionCatalogForm'
import { VoicemailSettingsCard } from '@/components/actions/VoicemailSettingsCard'
import {
  SelectableBox,
  SelectableBoxGroup,
} from '@/components/ui/selectable-box'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  useDropdownSourceFetching,
  type DropdownSourceFetchers,
} from '@/pages/actions/useDropdownSourceFetching'
import {
  ActionType,
  ActionSubtype,
  DEFAULT_TRANSFER_MESSAGE,
  type Integration,
  type SaveWorkflowActionDto,
  type WorkflowActionCatalogEntry,
  type WorkflowActionContactInput,
} from '@super/shared-types'

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2, 10)}`

const DEFAULT_TRANSFER: TransferSettings = {
  connectionMethod: 'SIMPLE_TRANSFER',
  preserveOriginalCallerId: false,
  declinedMessage: null,
  transferMessage: DEFAULT_TRANSFER_MESSAGE,
  voicemailDetectionEnabled: true,
  leaveVoicemailEnabled: true,
  voicemailMessage: null,
}

export function ActionDetailPage() {
  return (
    <FloatingActionIslandProvider>
      <DirectoryProvider>
        <ActionDetailPageContent />
      </DirectoryProvider>
    </FloatingActionIslandProvider>
  )
}

function ActionDetailPageContent() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const { roles, staff, loading: directoryLoading } = useDirectory()
  const isNew = !id || id === 'new'

  useDocumentHead({ title: isNew ? 'New action' : 'Edit action' })
  useHeaderExtra(
    <Link
      to={ROUTE_REGISTRY.NODE_ACTIONS.path}
      className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md hover:bg-accent transition-colors text-sm text-muted-foreground"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to actions
    </Link>,
    [],
  )

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [row, setRow] = useState<ActionRow>({
    id: newId(),
    type: 'TRANSFER',
    target: '',
  })
  const [transfer, setTransfer] = useState<TransferSettings>(DEFAULT_TRANSFER)
  const [actionGroupId] = useState(newId())
  const [targetError, setTargetError] = useState(false)
  const [nameError, setNameError] = useState(false)
  const [usageCount, setUsageCount] = useState(0)
  const [emptyAfterCount, setEmptyAfterCount] = useState(0)
  const baseline = useRef<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const targetSectionRef = useRef<HTMLDivElement>(null)

  const snapshot = useMemo(
    () => JSON.stringify({ name, description, row, transfer }),
    [name, description, row, transfer],
  )
  const isDirty = baseline.current !== snapshot

  const [availableIntegrations, setAvailableIntegrations] = useState<
    Integration[]
  >([])
  const [integrationsLoading, setIntegrationsLoading] = useState(true)
  const pushOptions = useMemo<PushOption[]>(
    () => buildPushOptions(availableIntegrations),
    [availableIntegrations],
  )

  const dropdownSourceFetchers: DropdownSourceFetchers = useMemo(() => {
    const stripData = (
      items: ({ id: string; label: string; sublabel?: string } | null)[],
    ): PushFieldDropdownItem[] =>
      items
        .filter(
          (item): item is { id: string; label: string; sublabel?: string } =>
            item !== null,
        )
        .map(item => ({
          id: item.id,
          label: item.label,
          sublabel: item.sublabel,
        }))

    let pipelinesPromise: ReturnType<
      typeof integrationsClient.hubspot.getTicketPipelines
    > | null = null
    const fetchTicketPipelines = () => {
      pipelinesPromise ??= (
        isSuperAdmin && currentCompany
          ? integrationsClient.hubspot.getAdminTicketPipelines(
              currentCompany.id,
            )
          : integrationsClient.hubspot.getTicketPipelines()
      ).catch(error => {
        pipelinesPromise = null
        throw error
      })
      return pipelinesPromise
    }

    return {
      'buildium-task-categories': async () => {
        const categories =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminTaskCategories(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getTaskCategories()
        return stripData(categories.map(mapTaskCategoryToItem))
      },
      'buildium-staff-users': async () => {
        const users =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminStaffUsers(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getStaffUsers()
        return stripData(users.map(mapStaffUserToItem))
      },
      'buildium-vendors': async () => {
        const vendors =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminVendors(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getVendors()
        return stripData(vendors.map(mapVendorToItem))
      },
      'hubspot-owners': async () => {
        const owners =
          isSuperAdmin && currentCompany
            ? await integrationsClient.hubspot.getAdminAssignees(
                currentCompany.id,
              )
            : await integrationsClient.hubspot.getAssignees()
        return stripData(owners.map(mapAssigneeToItem))
      },
      'hubspot-ticket-pipelines': async () => {
        const pipelines = await fetchTicketPipelines()
        return pipelines.map(p => ({ id: p.id, label: p.label }))
      },
      'hubspot-ticket-stages': async () => {
        const pipelines = await fetchTicketPipelines()
        return pipelines.flatMap(p =>
          p.stages.map(s => ({
            id: s.id,
            label: s.label,
            sublabel: p.label,
            groupId: p.id,
          })),
        )
      },
      'doorloop-users': async () => {
        const users =
          isSuperAdmin && currentCompany
            ? await integrationsClient.doorLoop.getAdminUsers(currentCompany.id)
            : await integrationsClient.doorLoop.getUsers()
        return stripData(users.map(mapAssigneeToItem))
      },
      'doorloop-vendors': async () => {
        const vendors =
          isSuperAdmin && currentCompany
            ? await integrationsClient.doorLoop.getAdminVendors(
                currentCompany.id,
              )
            : await integrationsClient.doorLoop.getVendors()
        return stripData(vendors.map(mapAssigneeToItem))
      },
    }
  }, [isSuperAdmin, currentCompany])

  const syntheticGroups = useMemo(
    () => [
      {
        id: actionGroupId,
        name: '',
        when: '',
        actions: [row],
        transfer: row.type === 'TRANSFER' ? transfer : undefined,
      },
    ],
    [actionGroupId, row, transfer],
  )
  const { dropdownItems, failedSources } = useDropdownSourceFetching(
    syntheticGroups,
    pushOptions,
    dropdownSourceFetchers,
    integrationsLoading,
  )

  // Load integrations.
  useEffect(() => {
    const companyId = currentCompany?.id
    if (!companyId) {
      setIntegrationsLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setIntegrationsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getPushEnabledIntegrationsAdmin(companyId)
          : await integrationsClient.getPushEnabledIntegrations()
        if (cancelled) return
        setAvailableIntegrations(integrations)
      } catch (error) {
        console.error('Failed to load integrations', error)
      } finally {
        if (!cancelled) setIntegrationsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, isSuperAdmin])

  const load = useCallback(async () => {
    if (isNew || !id) return
    const scoped = isSuperAdmin && currentCompany
    const entry: WorkflowActionCatalogEntry | null = scoped
      ? await actionsCatalogApiClient.getActionForCompanyByAdmin(
          id,
          currentCompany!.id,
        )
      : await actionsCatalogApiClient.getActionById(id)
    if (!entry) {
      toast.error('Action not found')
      navigate(pathFor(ROUTE_REGISTRY.NODE_ACTIONS.id))
      return
    }
    setName(entry.resolvedName ?? '')
    setDescription(entry.description ?? '')
    const editorGroups = workflowToEditorGroups(
      [
        {
          id: entry.id,
          name: null,
          selectionType: 'CONDITIONAL',
          selectionCriteria: null,
          actions: [
            {
              actionDefinition: { actionType: entry.actionType },
              actionType: entry.actionType,
              actionSubtype: (entry.actionSubtype ??
                null) as ActionSubtype | null,
              parameters: (entry.parameters ?? null) as Record<
                string,
                unknown
              > | null,
              integrationId: entry.integrationId,
            },
          ],
        },
      ],
      pushOptions,
      {
        contactsByGroupId: new Map([
          [
            entry.id,
            entry.contacts.map(c => ({
              id: c.id,
              actionGroupId: entry.id,
              actionType: c.actionType as ContactActionType | null,
              roleId: c.roleId,
              staffId: c.staffId,
              role: null,
              staff: null,
              createdAt: '',
            })),
          ],
        ]),
        roles,
        staff,
      },
    )
    const hydrated = editorGroups[0]
    const hydratedRow = hydrated?.actions.length
      ? hydrated.actions[0]
      : {
          id: newId(),
          type: entry.actionType as ActionRowType,
          target: '',
        }
    const hydratedTransfer = hydrated?.transfer ?? DEFAULT_TRANSFER
    setRow(hydratedRow)
    setTransfer(hydratedTransfer)
    const usage =
      isSuperAdmin && currentCompany
        ? await actionsCatalogApiClient.getActionUsageForCompanyByAdmin(
            id,
            currentCompany.id,
          )
        : await actionsCatalogApiClient.getActionUsage(id)
    setUsageCount(usage?.count ?? 0)
    setEmptyAfterCount(usage?.emptyAfterCount ?? 0)
    baseline.current = JSON.stringify({
      name: entry.resolvedName ?? '',
      description: entry.description ?? '',
      row: hydratedRow,
      transfer: hydratedTransfer,
    })
    setLoading(false)
  }, [
    id,
    isNew,
    isSuperAdmin,
    currentCompany,
    navigate,
    pushOptions,
    roles,
    staff,
  ])

  useEffect(() => {
    if (isNew && baseline.current === null) {
      baseline.current = snapshot
    }
  }, [isNew, snapshot])

  useEffect(() => {
    if (isNew) return
    if (integrationsLoading || directoryLoading) return
    void load()
  }, [isNew, integrationsLoading, directoryLoading, load])

  async function handleSave() {
    const issues: string[] = []
    const nameValid = name.trim().length > 0
    const targetEmpty =
      row.type === 'PUSH_INTEGRATION'
        ? (row.pushTargets ?? []).length === 0
        : !row.target.trim()
    if (!nameValid) issues.push('Name is required')
    if (targetEmpty) issues.push('Set a target')
    setNameError(!nameValid)
    setTargetError(targetEmpty)
    if (issues.length > 0) {
      toast.error(issues.join(' · '))
      if (!nameValid) {
        nameInputRef.current?.focus()
      } else if (targetEmpty) {
        const section = targetSectionRef.current
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'center' })
          section.querySelector<HTMLInputElement>('input,textarea')?.focus()
        }
      }
      return
    }
    setSaving(true)
    const { pairs } = editorToWorkflowGroups(
      syntheticGroups,
      roles,
      staff,
      availableIntegrations,
    )
    const apiActions = pairs[0]?.api.actions ?? []
    if (apiActions.length === 0) {
      setSaving(false)
      toast.error('Could not build the action payload')
      return
    }
    const channel =
      row.type === 'PUSH_INTEGRATION' ? null : contactChannelOf(row.type)
    const parsedMentions =
      row.type === 'PUSH_INTEGRATION'
        ? { roleIds: new Set<string>(), staffIds: new Set<string>() }
        : parseMentions(row.target, roles, staff)
    const contacts: WorkflowActionContactInput[] = [
      ...[...parsedMentions.roleIds].map(roleId => ({
        roleId,
        actionType: channel ?? null,
      })),
      ...[...parsedMentions.staffIds].map(staffId => ({
        staffId,
        actionType: channel ?? null,
      })),
    ]
    const dtos: SaveWorkflowActionDto[] = []
    for (const apiAction of apiActions) {
      const resolvedActionType =
        apiAction.actionDefinition?.actionType ?? apiAction.actionType
      if (!resolvedActionType) {
        setSaving(false)
        toast.error('Missing action type')
        return
      }
      dtos.push({
        name: name.trim(),
        description: description.trim() || null,
        actionType: resolvedActionType as ActionType,
        actionSubtype: (apiAction.actionSubtype ??
          null) as ActionSubtype | null,
        parameters: (apiAction.parameters ?? null) as Record<
          string,
          unknown
        > | null,
        integrationId: apiAction.integrationId ?? null,
        contacts,
      })
    }
    try {
      const payload = { existingId: isNew ? undefined : id!, actions: dtos }
      const result =
        isSuperAdmin && currentCompany
          ? await actionsCatalogApiClient.bulkSaveForCompany(
              currentCompany.id,
              payload,
            )
          : await actionsCatalogApiClient.bulkSave(payload)
      if (!result || result.length === 0) {
        toast.error('Save failed')
        return
      }
      const extras = result.length - (isNew ? 0 : 1)
      const message = isNew
        ? result.length === 1
          ? 'Action created'
          : `${result.length} actions created`
        : extras > 0
          ? `Action saved; ${extras} sibling${extras === 1 ? '' : 's'} added for extra recipients`
          : 'Action saved'
      toast.success(message)
      navigate(pathFor(ROUTE_REGISTRY.NODE_ACTIONS.id))
    } catch (error) {
      const message = describeSaveError(error)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  useHeaderRight(
    !isNew ? (
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => void handleDelete()}
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
        Delete
      </Button>
    ) : null,
    [isNew, id],
  )

  async function handleDelete() {
    if (!id || isNew) return
    const label = name.trim() || 'Untitled'
    const ok = await confirmDialog(
      buildCatalogDeleteMessage(label, usageCount, emptyAfterCount),
    )
    if (!ok) return
    const result =
      isSuperAdmin && currentCompany
        ? await actionsCatalogApiClient.deleteActionForCompany(
            id,
            currentCompany.id,
          )
        : await actionsCatalogApiClient.deleteAction(id)
    if (result.ok) {
      toast.success('Action deleted')
      navigate(pathFor(ROUTE_REGISTRY.NODE_ACTIONS.id))
      return
    }
    toast.error('Could not delete the action')
  }

  const hydrating = loading || directoryLoading || integrationsLoading
  if (hydrating && !isNew) {
    return (
      <div className="h-full overflow-y-auto pt-16">
        <div className="space-y-6 p-6 pt-0 pb-24 max-w-2xl mt-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24 max-w-2xl mt-6">
        <PageHeader title={isNew ? 'Create Action' : 'Edit Action'} />

        <Card>
          <CardContent className="pt-6">
            <ActionCatalogForm
              actionGroupId={actionGroupId}
              name={name}
              description={description}
              rows={[row]}
              transfer={transfer}
              onNameChange={next => {
                setName(next)
                if (next.trim()) setNameError(false)
              }}
              onDescriptionChange={setDescription}
              onRowChange={(_rowId, patch) => {
                setTargetError(false)
                setRow(prev => ({ ...prev, ...patch }) as ActionRow)
              }}
              nameError={nameError}
              targetErrors={
                targetError ? new Set<string>([row.id]) : new Set<string>()
              }
              disallowedTypesBase={new Set<ActionRowType>(['PHONE_CALL'])}
              canAddRows={false}
              pushOptions={pushOptions}
              pushFieldDropdownItems={dropdownItems}
              pushFieldFailedSources={failedSources}
              nameInputRef={nameInputRef}
              targetSectionRef={targetSectionRef}
            />
          </CardContent>
        </Card>

        {row.type === 'TRANSFER' && (
          <Card>
            <CardHeader>
              <CardTitle>Connection method</CardTitle>
            </CardHeader>
            <CardContent>
              <SelectableBoxGroup className="grid-cols-1">
                <SelectableBox
                  id={'SIMPLE_TRANSFER'}
                  selected={transfer.connectionMethod === 'SIMPLE_TRANSFER'}
                  onSelect={() =>
                    setTransfer(prev => ({
                      ...prev,
                      connectionMethod: 'SIMPLE_TRANSFER',
                    }))
                  }
                >
                  <div className="space-y-0.5">
                    <div className="font-medium">Simple transfer</div>
                    <div
                      className={cn(
                        'text-[11px]',
                        transfer.connectionMethod === 'SIMPLE_TRANSFER'
                          ? 'text-white/80 dark:text-foreground/80'
                          : 'text-muted-foreground',
                      )}
                    >
                      The agent conferences a live connection to a staff member.
                    </div>
                  </div>
                </SelectableBox>
                <SelectableBox
                  id={'AI_TRANSFER'}
                  selected={transfer.connectionMethod === 'AI_TRANSFER'}
                  onSelect={() =>
                    setTransfer(prev => ({
                      ...prev,
                      connectionMethod: 'AI_TRANSFER',
                    }))
                  }
                >
                  <div className="space-y-0.5">
                    <div className="font-medium">AI phone tree</div>
                    <div
                      className={cn(
                        'text-[11px]',
                        transfer.connectionMethod === 'AI_TRANSFER'
                          ? 'text-white/80 dark:text-foreground/80'
                          : 'text-muted-foreground',
                      )}
                    >
                      The agent briefs the recipient and asks for approval
                      before connecting.
                    </div>
                  </div>
                </SelectableBox>
              </SelectableBoxGroup>
              {transfer.connectionMethod === 'SIMPLE_TRANSFER' && (
                <>
                  <hr className="my-3 border-border" />
                  <label
                    className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5 cursor-pointer hover:bg-accent/40"
                    htmlFor="preserve-caller-id"
                  >
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">
                        Preserve original caller id
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Shows the caller&apos;s number instead of the agent
                        number.
                      </p>
                    </div>
                    <Switch
                      id="preserve-caller-id"
                      checked={transfer.preserveOriginalCallerId}
                      onCheckedChange={next =>
                        setTransfer(prev => ({
                          ...prev,
                          preserveOriginalCallerId: next,
                        }))
                      }
                    />
                  </label>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {row.type === 'TRANSFER' &&
          transfer.connectionMethod === 'AI_TRANSFER' && (
            <VoicemailSettingsCard
              value={{
                voicemailDetectionEnabled: transfer.voicemailDetectionEnabled,
                leaveVoicemailEnabled: transfer.leaveVoicemailEnabled,
                voicemailMessage: transfer.voicemailMessage,
              }}
              onChange={next =>
                setTransfer(prev => ({
                  ...prev,
                  voicemailDetectionEnabled: next.voicemailDetectionEnabled,
                  leaveVoicemailEnabled: next.leaveVoicemailEnabled,
                  voicemailMessage: next.voicemailMessage,
                }))
              }
            />
          )}
      </div>

      <FloatingActionIslandContent
        show={isDirty}
        componentId="action-detail-form"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
            <span className="font-medium">
              Saving "{name.trim() || 'Untitled'}" action
            </span>
          </div>
          <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
            <Button
              onClick={handleSave}
              disabled={saving || nameError || targetError}
              size="sm"
              variant="form"
            >
              <span>{saving ? 'Saving…' : isNew ? 'Create' : 'Update'}</span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
      <FloatingActionIsland />
    </div>
  )
}

function describeSaveError(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as { message?: string; issues?: string[] } | null
    if (body?.issues?.length) {
      return `${body.message ?? 'Save failed'}: ${body.issues.join('; ')}`
    }
    if (body?.message) return body.message
    return `Save failed (${error.status})`
  }
  if (error instanceof Error) return error.message
  return 'Save failed'
}
